import { useI18n, type I18nContextValue, type MessageKey } from '@ecom/i18n';
import { Badge, Card, EmptyState, type BadgeTone } from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useOrderCountsQuery } from '../api';
import {
  buildTodo,
  isAllClear,
  isNewShop,
  severityKey,
  type Loadable,
  type TodoInput,
  type TodoItem,
  type TodoSection,
  type TodoSectionId,
} from '../todo';

import { RecentActivity } from '@/features/analytics/components/RecentActivity';
import { useDeliveriesPendingQuery } from '@/features/delivery/api';
import { useJibexAccountQuery } from '@/features/delivery/jibex-account/api';
import type { JibexAccountView } from '@/features/delivery/jibex-account/types';
import { useInventoryOverviewQuery } from '@/features/inventory/api';
import { useActiveShop } from '@/features/shops/use-active-shop';
import { ApiError } from '@/lib/api-client';

const DOT_CLASSES: Record<BadgeTone, string> = {
  good: 'bg-good',
  warn: 'bg-warn',
  critical: 'bg-critical',
  info: 'bg-info',
  accent: 'bg-accent',
  neutral: 'bg-inkFaint',
};

const SECTION_TITLE: Record<TodoSectionId, MessageKey> = {
  orders: 'merchant.todo.section.orders',
  stock: 'merchant.todo.section.stock',
  delivery: 'merchant.todo.section.delivery',
  jibex: 'merchant.todo.section.jibex',
};

/** A query's state as a `Loadable`: a 403 is « your role has no access », never a failure to report as one. */
function loadable<T>(query: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: T | undefined;
}): Loadable<T> {
  if (query.error instanceof ApiError && query.error.status === 403) return { state: 'forbidden' };
  if (query.isError) return { state: 'error' };
  if (query.isPending || query.data === undefined) return { state: 'pending' };
  return { state: 'ready', data: query.data };
}

/**
 * « À traiter » — what needs attention now, from orders, stock, delivery and the Jibex account. Everything is
 * derived from data the dashboard already reads; nothing is stored. It replaced the old « Notifications » page,
 * which was only the log of emails that were never delivered. The chronological feed (« Activité récente ») sits
 * underneath.
 */
export function TodoPage(): ReactElement {
  const { t } = useI18n();
  const shop = useActiveShop();
  const isOwner = shop.activeMembership?.roleKey === 'merchant_owner';

  const orders = useOrderCountsQuery();
  const inventory = useInventoryOverviewQuery();
  const shipmentsQuery = useDeliveriesPendingQuery();
  const jibexQuery = useJibexAccountQuery(isOwner);

  const jibex: Loadable<JibexAccountView> | undefined = isOwner ? loadable(jibexQuery) : undefined;

  const input: TodoInput = {
    orders,
    stock: inventory.isForbidden
      ? { state: 'forbidden' }
      : inventory.isError
        ? { state: 'error' }
        : inventory.isPending
          ? { state: 'pending' }
          : { state: 'ready', data: inventory.rows },
    shipments: loadable(shipmentsQuery),
    jibex,
  };
  const sections = buildTodo(input);
  const clear = isAllClear(sections);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('merchant.todo.title')}</h1>
        <p className="text-sm text-inkFaint">{t('merchant.todo.subtitle')}</p>
      </div>

      <div className="flex flex-col gap-6">
        {clear ? (
          <>
            {isNewShop(input) ? (
              <NewShop t={t} />
            ) : (
              <EmptyState title={t('merchant.todo.allClear')} />
            )}
            {/* What this role cannot read is still said, under the all-clear message about what it can. */}
            {sections
              .filter((section) => section.status === 'forbidden')
              .map((section) => (
                <Section key={section.id} section={section} t={t} />
              ))}
          </>
        ) : (
          sections
            .filter((section) => section.status !== 'ready' || section.items.length > 0)
            .map((section) => <Section key={section.id} section={section} t={t} />)
        )}
      </div>

      <RecentActivity />
    </div>
  );
}

const PRIMARY_LINK =
  'inline-flex items-center rounded-md bg-accent px-3.5 py-2 text-sm font-semibold text-white hover:bg-accentStrong';
const SECONDARY_LINK =
  'inline-flex items-center rounded-md border border-line px-3.5 py-2 text-sm font-semibold text-ink hover:bg-surface2';

/** A shop with no product and no order: « nothing to do » would be false — everything is still to do. */
function NewShop({ t }: { readonly t: I18nContextValue['t'] }): ReactElement {
  return (
    <EmptyState
      title={t('merchant.todo.newShop.title')}
      description={t('merchant.todo.newShop.description')}
      action={
        <div className="flex flex-wrap justify-center gap-2">
          <Link to="/products/new" className={PRIMARY_LINK}>
            {t('merchant.todo.newShop.addProduct')}
          </Link>
          <Link to="/store" className={SECONDARY_LINK}>
            {t('merchant.todo.newShop.completeShop')}
          </Link>
        </div>
      }
    />
  );
}

function Section({
  section,
  t,
}: {
  readonly section: TodoSection;
  readonly t: I18nContextValue['t'];
}): ReactElement {
  return (
    <section aria-labelledby={`todo-${section.id}`}>
      <h2 id={`todo-${section.id}`} className="mb-2 font-display text-lg font-bold text-ink">
        {t(SECTION_TITLE[section.id])}
      </h2>
      {section.status === 'pending' ? (
        <p className="text-sm text-inkFaint">{t('merchant.todo.loading')}</p>
      ) : section.status === 'forbidden' ? (
        <p className="text-sm text-inkFaint">{t('merchant.todo.forbidden')}</p>
      ) : section.status === 'error' ? (
        <p role="alert" className="text-sm text-critical">
          {t('merchant.todo.sectionError')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {section.items.map((item) => (
            <Item key={item.id} item={item} t={t} />
          ))}
        </ul>
      )}
    </section>
  );
}

function Item({
  item,
  t,
}: {
  readonly item: TodoItem;
  readonly t: I18nContextValue['t'];
}): ReactElement {
  return (
    <li>
      {/* The browser's own focus ring (tokens.css `:focus-visible`) is kept, and offset so it clears the card. */}
      <Link
        to={item.href}
        className="block rounded-xl2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <Card className="flex items-start gap-3 p-4 transition-colors hover:bg-surface2">
          <span
            className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT_CLASSES[item.tone]}`}
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1">
            <span className="mb-1 block">
              <Badge tone={item.tone}>{t(severityKey(item.tone))}</Badge>
            </span>
            <span className="block text-sm font-semibold text-ink">
              {t(item.titleKey, item.params)}
            </span>
            <span className="block text-sm text-inkFaint">{t(item.descriptionKey)}</span>
            {item.detail !== null ? (
              <span className="mt-0.5 block text-xs text-inkSoft">{item.detail}</span>
            ) : null}
          </span>
        </Card>
      </Link>
    </li>
  );
}
